import { Radio, RadioChangeEvent, RadioGroupProps } from 'antd';
import clx from 'classnames';
import { omit } from 'lodash';
import { useCallback } from 'react';
import styled from 'styled-components';
import { useInputController } from '../hooks/useInputController';
import { IsRadioOrCheckboxGroupElementSymbol } from '../item/symbols/isRadioOrCheckboxGroupElement';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility';
import { useFormItemEnabled } from '../item/useFormItemEnabled';

const StyledRadioGroup = styled(Radio.Group)`
  flex-flow: wrap;
  &.vertical {
    display: flex;
    flex-direction: column;
    > label {
      width: max-content;
      max-width: 100%;
    }
  }
  &.read-only {
    pointer-events: none;
  }
`;

export interface IRadioGroupInputProps extends Omit<RadioGroupProps, 'name'> {
  name: string;

  /**
   * Permette lo sviluppo in verticale delle opzioni
   * -> un opzione per ogni riga
   */
  vertical?: boolean; // TODO: da implementare
  /**
   * Aggungiamo proprietà readOnly non gestita da antd
   */
  readOnly?: boolean;
  /**
   * in caso che dobbiamo trasformare il valore per l'input
   */
  transformValue?: (value: any) => any;
}

export function RadioGroupInput(props: IRadioGroupInputProps) {
  const { name, vertical, readOnly, className, transformValue, ...inputProps } =
    props;

  const { enabled, layoutEnabled } = useFormItemEnabled();

  const {
    field: { value, onChange, onBlur, ref },
  } = useInputController(name, { enabled });
  const transformedValue = transformValue?.(value) ?? value;

  const handleChange = useCallback((e: RadioChangeEvent) => {
    onChange(e.target.value);
  }, []);

  const accessibility = useFormItemAccessibility();

  return (
    <div
      role="radiogroup"
      {...omit(accessibility.attributes, 'id')}
      aria-orientation={vertical ? 'vertical' : 'horizontal'}
      aria-labelledby={accessibility.labelId}
    >
      <StyledRadioGroup
        size="large"
        disabled={enabled === false || layoutEnabled === false}
        {...inputProps}
        value={transformedValue}
        onBlur={onBlur}
        ref={r => {
          ref?.({
            ...r,
            focus: () => r?.querySelector('input')?.focus(),
          });
        }}
        name={name}
        onChange={readOnly ? undefined : handleChange}
        className={clx(className, { vertical, 'read-only': readOnly })}
      />
    </div>
  );
}

RadioGroupInput[IsRadioOrCheckboxGroupElementSymbol] = true;
